// LOWESS.ts
// Robust LOWESS / LOESS implementation (linear)
// Author: Mario Rainer

export type Point = {
  x: number;
  y: number;
};

export interface LowessOptions {
  span?: number; // Anteil der Nachbarschaft (0 < span <= 1)
  robustIterations?: number; // Anzahl robuster Iterationen
}

export class LOWESS {
  static smooth(data: Point[], options: LowessOptions = {}): Point[] {
    const span = options.span ?? 0.02;
    const robustIterations = options.robustIterations ?? 2;

    if (!data || data.length < 3) return data ?? [];

    const sorted = [...data].sort((a, b) => a.x - b.x);
    const n = sorted.length;
    const k = Math.max(2, Math.floor(span * n));

    const yhat = new Array<number>(n).fill(0);
    let robustWeights = new Array<number>(n).fill(1);

    for (let iter = 0; iter <= robustIterations; iter++) {
      for (let i = 0; i < n; i++) {
        const distances = sorted.map(p => Math.abs(p.x - sorted[i].x));
        const sortedDistances = [...distances].sort((a, b) => a - b);
        const bandwidth = sortedDistances[k] || sortedDistances[sortedDistances.length - 1] || 1;

        let sumW = 0;
        let sumWX = 0;
        let sumWY = 0;
        let sumWXX = 0;
        let sumWXY = 0;

        for (let j = 0; j < n; j++) {
          const d = distances[j] / bandwidth;
          if (d >= 1) continue;

          const w = this.tricube(d) * robustWeights[j];

          const x = sorted[j].x;
          const y = sorted[j].y;

          sumW += w;
          sumWX += w * x;
          sumWY += w * y;
          sumWXX += w * x * x;
          sumWXY += w * x * y;
        }

        const denom = sumW * sumWXX - sumWX * sumWX;

        if (Math.abs(denom) < 1e-12) {
          yhat[i] = sorted[i].y;
        } else {
          const a = (sumWXX * sumWY - sumWX * sumWXY) / denom;
          const b = (sumW * sumWXY - sumWX * sumWY) / denom;
          yhat[i] = a + b * sorted[i].x;
        }
      }

      if (iter === robustIterations) break;

      const residuals = sorted.map((p, i) => Math.abs(p.y - yhat[i]));

      const medianResidual = this.median(residuals) || 1e-12;

      robustWeights = residuals.map(r => {
        const u = r / (6 * medianResidual);
        if (u >= 1) return 0;
        return Math.pow(1 - u * u, 2);
      });
    }

    return sorted.map((p, i) => ({
      x: p.x,
      y: yhat[i],
    }));
  }

  private static tricube(x: number): number {
    const tmp = 1 - Math.pow(Math.abs(x), 3);
    return Math.pow(tmp, 3);
  }

  private static median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  }
}
